import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import Select from 'react-select';
import {
  Row,
  Col,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Card,
  CardBody,
  FormGroup,
} from 'reactstrap';
import CheckBox from '../../../../shared/components/CheckBox';
import CheckboxFilter from '../../../../shared/components/CheckboxFilter';
import TimePeriodFilterPipeline from './TimePeriodFilterPipeline';
import {
  selectFilters,
  selectAvailableCountries,
  selectLoading,
  updateFilter,
  setDateRange,
} from '../../../../redux/reducers/brand/ConfirmedDealsReducer';
import ConversionToggles from './ConversionToggles';

// Convert array filter state to CheckboxFilter object prop ([] = all selected = all true)
const toCheckboxFilterProp = (options, selectedArray) =>
  options.reduce((acc, opt) => {
    acc[opt] = selectedArray.length === 0 || selectedArray.includes(opt);
    return acc;
  }, {});

// Convert CheckboxFilter result back to array (all true = [] meaning "no filter")
const fromCheckboxFilterData = data =>
  Object.entries(data).filter(([, v]) => v).map(([k]) => k).length === Object.keys(data).length
    ? []
    : Object.entries(data).filter(([, v]) => v).map(([k]) => k);

const NEW_REBUY_OPTIONS = ['New', 'Rebuy'];
const CONFIDENCE_OPTIONS = ['High', 'Medium', 'Low', 'Published'];

const PipelineFilters = ({
  filters,
  availableCountries,
  loading,
  dispatch,
}) => {
  const handleTimePeriodChange = (key, value, range) => {
    // When a preset range is selected (not custom), both dates are set by the TimePeriodFilter
    // We need to calculate both dates based on the range to avoid using stale filter values
    let startDate;
    let endDate;

    if (range) {
      // Preset range selected - recalculate both dates based on range
      switch (range) {
        case 'this_month':
          startDate = moment().startOf('month').format('YYYY-MM-DD');
          endDate = moment().endOf('month').format('YYYY-MM-DD');
          break;
        case 'next_month':
          startDate = moment().add(1, 'month').startOf('month').format('YYYY-MM-DD');
          endDate = moment().add(1, 'month').endOf('month').format('YYYY-MM-DD');
          break;
        case 'following_month':
          startDate = moment().add(2, 'months').startOf('month').format('YYYY-MM-DD');
          endDate = moment().add(2, 'months').endOf('month').format('YYYY-MM-DD');
          break;
        case 'plus_90_days':
          startDate = moment().startOf('day').format('YYYY-MM-DD');
          endDate = moment().add(90, 'days').endOf('day').format('YYYY-MM-DD');
          break;
        case 'custom':
          startDate = moment().startOf('year').format('YYYY-MM-DD');
          endDate = moment().endOf('year').format('YYYY-MM-DD');
          break;
        default:
          startDate = moment().startOf('month').format('YYYY-MM-DD');
          endDate = moment().endOf('month').format('YYYY-MM-DD');
      }
      // Only dispatch once when we have both dates (on endDate call)
      if (key === 'endDate') {
        dispatch(setDateRange({ startDate, endDate, timePeriod: range }));
      }
    } else {
      // Custom date or manual change - preserve the other date from filters
      if (key === 'startDate') {
        dispatch(setDateRange({
          startDate: moment(value).format('YYYY-MM-DD'),
          endDate: filters.endDate,
          timePeriod: range || 'custom',
        }));
      } else if (key === 'endDate') {
        dispatch(setDateRange({
          startDate: filters.startDate,
          endDate: moment(value).format('YYYY-MM-DD'),
          timePeriod: range || 'custom',
        }));
      }
    }
  };

  const handleSearchChange = (e) => {
    dispatch(updateFilter('search', e.target.value));
  };

  return (
    <Card className="mb-0">
      <CardBody className='pb-0'>
        <Row className="mb-3">
          <Col md={12}>
            <TimePeriodFilterPipeline
              setValue={handleTimePeriodChange}
              isLoading={loading}
              selectedButtonInitial={filters.timePeriod}
              startDateInitial={filters.startDate}
              endDateInitial={filters.endDate}
            />
          </Col>
        </Row>
        <Row>
          <Col md={3}>
            <div>
              <InputGroup>
                <Input
                  className="form-control-sm form-control"
                  name="search"
                  type="search"
                  autoComplete="off"
                  placeholder="Search creator or code..."
                  value={filters.search}
                  onChange={handleSearchChange}
                />
                <InputGroupAddon addonType="append">
                  <InputGroupText>
                    <i
                      className="fa fa-search"
                      aria-hidden="true"
                      style={{ color: '#70bbfd', fontSize: 12 }}
                    />
                  </InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            </div>
          </Col>
          <Col md={6}>
            <div className="d-flex align-items-center flex-wrap" style={{ marginTop: '-8px' }}>
              <CheckboxFilter
                filterProp={toCheckboxFilterProp(NEW_REBUY_OPTIONS, filters.newOrRebuy)}
                filterTitle="New/Rebuy"
                filterAction={({ data }) => dispatch(updateFilter('newOrRebuy', fromCheckboxFilterData(data)))}
              />
              <CheckboxFilter
                filterProp={toCheckboxFilterProp(CONFIDENCE_OPTIONS, filters.confidence)}
                filterTitle="Confidence"
                filterAction={({ data }) => dispatch(updateFilter('confidence', fromCheckboxFilterData(data)))}
              />
              <div className="ml-3 d-flex align-items-center" style={{ marginTop: '-8px' }}>
                <CheckBox
                  name="excludePublished"
                  label="Exclude Published Content"
                  value={filters.excludePublished}
                  onChange={(e) => {
                    const isEvent = e && e.target;
                    if (isEvent) {
                      dispatch(updateFilter('excludePublished', !filters.excludePublished));
                    }
                  }}
                  className="inline"
                  defaultChecked={false}
                />
              </div>
            </div>
          </Col>
          <Col md={3}>
            <div style={{ minWidth: '250px' }}>
              <FormGroup className="mb-0">
                <Select
                  isMulti
                  name="countrySelect"
                  isClearable
                  value={availableCountries.filter(country => filters.country.includes(country.value))}
                  options={availableCountries}
                  onChange={(data) => {
                    const selectedCountries = data ? data.map(item => item.value) : [];
                    dispatch(updateFilter('country', selectedCountries));
                  }}
                  placeholder="Program Countries"
                  styles={{
                    multiValue: styles => ({
                      ...styles,
                      backgroundColor: '#70bbfd',
                    }),
                    multiValueLabel: styles => ({
                      ...styles,
                      color: 'white',
                    }),
                    multiValueRemove: styles => ({
                      ...styles,
                      color: 'white',
                      ':hover': {
                        backgroundColor: '#a2e1fd',
                        color: '#70bbfd',
                      },
                    }),
                    control: styles => ({
                      ...styles,
                      minHeight: '31px',
                      height: '31px',
                    }),
                    valueContainer: styles => ({
                      ...styles,
                      height: '31px',
                      padding: '0 6px',
                    }),
                    input: styles => ({
                      ...styles,
                      margin: '0px',
                    }),
                    indicatorSeparator: styles => ({
                      ...styles,
                      display: 'none',
                    }),
                    indicatorsContainer: styles => ({
                      ...styles,
                      height: '31px',
                    }),
                  }}
                />
              </FormGroup>
            </div>
          </Col>
        </Row>
        <Row className="mb-2">
          <Col md={12}>
            <ConversionToggles filters={filters} dispatch={dispatch} />
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

PipelineFilters.propTypes = {
  filters: PropTypes.shape({
    timePeriod: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    search: PropTypes.string,
    newOrRebuy: PropTypes.arrayOf(PropTypes.string),
    confidence: PropTypes.arrayOf(PropTypes.string),
    country: PropTypes.arrayOf(PropTypes.string),
    excludePublished: PropTypes.bool,
    includeHistoricalConversions: PropTypes.bool,
    includeFutureConversions: PropTypes.bool,
    includePreviouslyPublishedConversions: PropTypes.bool,
  }).isRequired,
  availableCountries: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })).isRequired,
  loading: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  filters: selectFilters(state),
  availableCountries: selectAvailableCountries(state),
  loading: selectLoading(state),
});

export default connect(mapStateToProps)(PipelineFilters);
